"use client";

import {
  MotionConfig,
  motion,
  type Transition,
  type Variant,
  type Variants,
} from "motion/react";
import {
  createContext,
  isValidElement,
  Children,
  cloneElement,
  useContext,
  useState,
  type MouseEventHandler,
  type ReactNode,
  type ReactElement,
} from "react";

type DisclosureVariants = {
  expanded: Variant;
  collapsed: Variant;
};

type DisclosureContextValue = {
  open: boolean;
  toggle: () => void;
  variants?: DisclosureVariants;
};

const DisclosureContext = createContext<DisclosureContextValue | undefined>(
  undefined,
);

function useDisclosure() {
  const context = useContext(DisclosureContext);
  if (!context)
    throw new Error("Disclosure components must be nested inside Disclosure.");
  return context;
}

function joinClassNames(...classNames: Array<string | undefined>) {
  return classNames.filter(Boolean).join(" ");
}

export function Disclosure({
  children,
  className,
  defaultOpen = false,
  onOpenChange,
  open,
  transition,
  variants,
}: {
  children: ReactNode;
  className?: string;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
  transition?: Transition;
  variants?: DisclosureVariants;
}) {
  const [internalOpen, setInternalOpen] = useState(open ?? defaultOpen);
  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : internalOpen;
  const toggle = () => {
    const nextOpen = !isOpen;
    if (!isControlled) setInternalOpen(nextOpen);
    onOpenChange?.(nextOpen);
  };

  return (
    <MotionConfig transition={transition}>
      <div className={className} data-state={isOpen ? "open" : "closed"}>
        <DisclosureContext.Provider value={{ open: isOpen, toggle, variants }}>
          {children}
        </DisclosureContext.Provider>
      </div>
    </MotionConfig>
  );
}

export function DisclosureTrigger({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  const { open, toggle } = useDisclosure();

  return Children.map(children, (child) => {
    if (!isValidElement(child)) return child;
    const element = child as ReactElement<{
      "aria-expanded"?: boolean;
      onClick?: MouseEventHandler<HTMLElement>;
      className?: string;
    }>;
    return cloneElement(element, {
      "aria-expanded": open,
      className: joinClassNames(className, element.props.className),
      onClick: element.props.onClick ?? toggle,
    });
  });
}

export function DisclosureContent({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  const { open, variants } = useDisclosure();
  const baseVariants: Variants = {
    expanded: { height: "auto", opacity: 1 },
    collapsed: { height: 0, opacity: 0 },
  };
  const combinedVariants: Variants = {
    expanded: { ...baseVariants.expanded, ...variants?.expanded },
    collapsed: { ...baseVariants.collapsed, ...variants?.collapsed },
  };

  return (
    <div className={joinClassNames("disclosure-content", className)}>
      <motion.div
        animate={open ? "expanded" : "collapsed"}
        initial={open ? "expanded" : "collapsed"}
        variants={combinedVariants}
      >
        {children}
      </motion.div>
    </div>
  );
}
